import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {CheckInCheckOut} from '../Policies/CheckInCheckOut'
import {PolicySubSection} from '../Policies/types'
import {
  getDescription,
  getTitle,
  isSubSectionAvailable,
  policySubSections
} from '../Policies/utils'

const PolicyTitle = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s300};
`

const PolicyButton = styled(ButtonLink)`
  width: fit-content;
  margin-top: ${cssTheme.layout.spacing.s400};
`

const PolicyItem = ({
  section,
  hotel,
  dataId
}: {
  section: PolicySubSection
  hotel: ContentHotel
  dataId?: string
}) => {
  const {formatMessage} = useIntl()

  const showOverlay = useShowOverlay()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const showSubSection = e => {
    e.preventDefault()

    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsPropertyPolicy,
        action: Action.Clicked,
        payload: {
          selectedElement: section
        }
      })
    )

    showOverlay({
      type: OverlayType.HotelPolicies,
      params: {
        policySubSection: section
      }
    })
  }
  const showDepositAmount =
    section === PolicySubSection.SecurityDeposit &&
    Boolean(hotel.deposit?.value)

  return (
    <Box display="flex" flexDirection="column" flex="1" alignItems="flex-start">
      <PolicyTitle variant="titleL" dataId={dataId}>
        {formatMessage(getTitle(section))}
      </PolicyTitle>
      <Text variant="bodyM">
        {showDepositAmount
          ? `${hotel.deposit?.value?.currency} ${hotel.deposit?.value?.amount} `
          : null}
        {formatMessage(getDescription(section, showDepositAmount))}
      </Text>
      <PolicyButton
        variant="primary"
        size="lg"
        isCompact
        onClick={showSubSection}
        href="#"
        role="button"
      >
        <FormattedMessageWrapper id="showMore" defaultMessage="Show more" />
      </PolicyButton>
    </Box>
  )
}

interface Props {
  hotel: ContentHotel
}
export const PoliciesSection: React.FC<Props> = ({hotel}) => {
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})

  const dispatchTeamEvent = useDispatchTeamEvent()

  useEffect(() => {
    if (inView)
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsPropertyPolicySection,
          action: Action.Viewed
        })
      )
  }, [dispatchTeamEvent, inView])

  const policySubSectionsKeys = Object.keys(policySubSections).filter(
    (section: PolicySubSection) =>
      isSubSectionAvailable(hotel, section) &&
      section !== PolicySubSection.CheckInInstructions
  )
  const hasCheckInInstructions = isSubSectionAvailable(
    hotel,
    PolicySubSection.CheckInInstructions
  )

  return (
    <div ref={ref}>
      <Box paddingBottom="s900">
        <Box display="flex" flexDirection="column" gap="s600">
          <Text variant="titleXL" tag="h2">
            <FormattedMessageWrapper
              defaultMessage="Property policies"
              id="propertyPolicy"
            />
          </Text>

          <Box display="flex" flexDirection="column" gap="s800">
            <Box
              display="flex"
              justifyContent={
                hasCheckInInstructions ? 'space-between' : 'flex-start'
              }
            >
              {hasCheckInInstructions && (
                <PolicyItem
                  section={PolicySubSection.CheckInInstructions}
                  hotel={hotel}
                />
              )}
              {hotel.checkInTime && hotel.checkOutTime && (
                <Box flex={hasCheckInInstructions ? '1' : '0 0 50%'}>
                  <CheckInCheckOut
                    isLoading={false}
                    layout="secondary"
                    hotel={hotel}
                  />
                </Box>
              )}
            </Box>

            {policySubSectionsKeys.length > 0 && (
              <>
                <Divider />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gap={policySubSectionsKeys.length > 2 ? 's900' : 's000'}
                >
                  {policySubSectionsKeys.map((section: PolicySubSection) => (
                    <PolicyItem
                      key={section}
                      hotel={hotel}
                      section={section}
                      dataId={section}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </div>
  )
}
